import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import CodeMirror from '@uiw/react-codemirror';
import { json } from '@codemirror/lang-json';
import { oneDark } from '@codemirror/theme-one-dark';
import { Markup } from './types';
import './Admin.css';

interface MarkupModalProps {
    markup: Markup | null;
    onClose: () => void;
    onSave: (markup: Markup) => void;
}

const MarkupModal: React.FC<MarkupModalProps> = ({ markup, onClose, onSave }) => {
    const [jsonData, setJsonData] = useState<string>('');
    const [isValidJson, setIsValidJson] = useState<boolean>(true);

    useEffect(() => {
        if (markup) {
            setJsonData(JSON.stringify(markup, null, 2));
        } else {
            setJsonData(JSON.stringify({
                hash: '',
                sceneName: '',
                mark: {
                    id: '',
                    markType: '',
                    data: {}
                }
            }, null, 2));
        }
    }, [markup]);

    const handleSave = () => {
        try {
            const parsedData = JSON.parse(jsonData);
            onSave(parsedData);
            onClose();
        } catch (error) {
            toast.error('Invalid JSON format', { closeButton: false, hideProgressBar: true, autoClose: 3000 });
        }
    };

    const handleChange = (value: string) => {
        setJsonData(value);
        try {
            JSON.parse(value);
            setIsValidJson(true);
        } catch {
            setIsValidJson(false);
        }
    };

    return (
        <div className="modal-overlay">
            <div className="modal-content">
                <div className="modal-header">
                    <h3>{markup ? 'Edit Markup' : 'Create New Markup'}</h3>
                    <button className="close-button" onClick={onClose}>×</button>
                </div>
                <div className="modal-body">
                    <div className="form-group">
                        <label>JSON Data:</label>
                        <div className={`codemirror-container ${!isValidJson ? 'invalid' : ''}`}>
                            <CodeMirror
                                value={jsonData}
                                height="1000px"
                                theme={oneDark}
                                extensions={[json()]}
                                onChange={handleChange}
                            />
                        </div>
                        {!isValidJson && (
                            <div className="error-message">Invalid JSON format</div>
                        )}
                    </div>
                </div>
                <div className="modal-footer">
                    <button 
                        className="cancel-button" 
                        onClick={onClose}
                    >
                        Cancel
                    </button>
                    <button 
                        className="submit-button" 
                        onClick={handleSave}
                        disabled={!isValidJson}
                    >
                        Save
                    </button>
                </div>
            </div>
        </div>
    );
};

export default MarkupModal; 